import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type RefObject } from 'react'
import { flushSync } from 'react-dom'
import { useNavigate, useParams } from 'react-router'
import { ChevronDown } from 'lucide-react'
import { Async } from '../../platform/async/Async'
import { emit } from '../../platform/analytics/analytics'
import { DisabledReason } from '../../platform/access/DisabledReason'
import { Skeleton } from '../../platform/async/Skeleton'
import { Button } from '../../platform/primitives/Button'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { DataGrid, type Column } from '../../platform/primitives/DataGrid'
import { Panel } from '../../platform/primitives/Panel'
import { Select } from '../../platform/primitives/Select'
import { Icon } from '../../platform/icons/Icon'
import { CategoryIcon } from '../../platform/icons/CategoryIcon'
import { BackLink } from '../../platform/primitives/BackLink'
import { Money } from '../../platform/primitives/Money'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { useToast } from '../../platform/feedback/ToastProvider'
import '../../platform/notes.css'
import type { Category } from '../categories/categoryTree'
import { useCategories } from '../categories/useCategories'
import { useHouseholdId } from '../household/useHousehold'
import { useMembers } from '../household/useMembers'
import { accountHealth } from './accountHealth'
import { AccountHero } from './AccountHero'
import { accountTypeLabel, isCreditCard } from './accountGroups'
import { formatRelative } from './accountsStaleLabels'
import { BalanceHistory } from './BalanceHistory'
import { StatementCycleCard } from './StatementCycleCard'
import { providerLabel } from './providers/registry'
import { accountRemovalCopy, useAccountDeleteFlow } from './useAccountDeleteFlow'
import { useAccountTransactions, type AccountTransaction } from './useAccountTransactions'
import { summarizeSync, useSyncItem } from './useSyncItem'
import { statementErrorKind } from './statementCyclesWire'
import { AccountsError, useAccount, useSetBeneficialOwner, useSetStatementDay, type Account } from './useAccounts'
import './accounts.css'

/**
 * The "Last synced" row's own VALUE — "4 min ago · next sync available
 * 9:47" — the design gap-fill pass, 2026-09-17, replacing the artboard's
 * "Sync frequency · Every 6 hours", which describes nothing real: cadence
 * is a single global 900s webhook-driven interval, deliberately unsettable,
 * not a per-account setting. Both halves are real fields —
 * `account.lastSyncedAt` was already on `AccountResponse`; `nextSyncAt` is
 * this session's own last sync outcome (`useSyncItem.ts`), present only
 * after a Sync-now click has actually returned one — so the second clause
 * is omitted, not guessed, until that happens.
 *
 * **No "Last synced" prefix here — the row's own label owns those words.**
 * An earlier version of this function returned the full sentence, which the
 * row then rendered NEXT TO a label reading "Last synced", producing "Last
 * synced　Last synced 171d ago" (team-lead finding, screenshotted against a
 * linked account). This is this function's only caller; if a second one
 * ever needs the full sentence (standing alone, no label beside it), that
 * is `heroSyncLabel`'s job (`AccountHero.tsx`) — a DIFFERENT function for a
 * DIFFERENT context, not something to unify with this one, since the pill
 * has no label to lean on and this row does.
 */
const NEXT_SYNC_TIME = new Intl.DateTimeFormat('en-GB', { hour: 'numeric', minute: '2-digit' })

function lastSyncedValue(account: Account, nextSyncAt: Date | null): string {
  if (account.lastSyncedAt === null) return 'Never'
  const base = formatRelative(account.lastSyncedAt)
  return nextSyncAt ? `${base} · next sync available ${NEXT_SYNC_TIME.format(nextSyncAt)}` : base
}

/** The menu's sentinel for "no beneficial owner set" — a real string value
 *  is needed to compare against `account.ownerId` and to send over the
 *  wire, and `null` is not one; same shape as `MerchantsToolbar`'s `ALL`
 *  for the identical reason. Mapped back to `null` at the one call site
 *  that sends it as `beneficial_owner_id`. */
const UNCLAIMED = '__unclaimed__'

/**
 * The health note — `accountHealth()`'s `reauth` kind only, rendered with the
 * SAME classes `<Async>`'s own stale branch uses (`notes.css`), because a
 * reauth-required account is M5-R13's "stale with its date, not an error":
 * the data on screen is still true, and this is a caution rather than a
 * failure regardless of what the surface's own fetch just did — a recently-
 * synced account whose Item needs reconnecting is `success` at the surface
 * level (the GET worked) and `reauth` here, and both facts are real.
 *
 * **Deliberately silent on `health.kind === 'stale'`.** Found live (browser
 * check, 2026-09-11): `<Async>`'s OWN stale branch already renders "Showing
 * data from …" whenever `useAccount`'s surface state is `stale`, from the
 * exact same `last_synced_at` this function's `stale` branch would read —
 * rendering it again here repeated the identical sentence twice on any
 * account stale for the plain reason (no reauth involved). `accountHealth`
 * still returns `stale` for consumers with no such built-in banner — a list
 * row, say — this screen already has one and does not need a second.
 */
function HealthNote({ account }: { account: Account }) {
  const health = accountHealth(account)

  if (health.kind !== 'reauth') return null

  // N1 (architect review, #396, non-blocking): no trailing "Last synced …"
  // clause here. A reauth account whose sync is ALSO past STALE_AFTER_MS is
  // real -- reauth outranks stale in accountHealth()'s own return value, but
  // <Async>'s surface-level staleness is judged purely by date and fires
  // independently -- and printing the date here duplicated its "Showing
  // data from …" banner on exactly that account.
  return (
    <p className="note note--stale">
      {`${health.institution ?? 'This connection'} needs reconnecting.`}
    </p>
  )
}

/**
 * The Owner row — a definition row whose VALUE is a dropdown trigger, not a
 * plain span (user finding, design gap-fill round 6, 2026-09-17, replacing
 * round 5's collapsed-row-plus-revealed-`SegmentedControl`: "it is not in a
 * segment component. You could create a dropdown or something different.").
 *
 * `account.ownerName` is the SAME resolution `toAccount()` already computes
 * (`display_name ?? email`, `useAccounts.ts`) — reading it here rather than
 * re-deriving it means this row and the account card's own second line can
 * never disagree.
 */
function OwnerRow({ account }: { account: Account }) {
  return (
    <div className="account-detail__row account-detail__row--inline account-detail__row--owner">
      <span className="account-detail__row-label">Owner</span>
      <OwnerMenu account={account} />
    </div>
  )
}

const NOTE_ID = 'statement-day-note'
const STATEMENT_DAYS = Array.from({ length: 31 }, (_, i) => i + 1)

/**
 * The credit-card Statement day row. Rendered by the caller only when
 * `isCreditCard(account.type)`: the backend 422s a day on any other type.
 *
 * No ownership check: `GET /accounts/{id}` is owner-filtered by the backend, so
 * whoever sees this page owns the account. `account.ownerId` is the BENEFICIAL
 * owner and must never gate this row. The select is controlled by the
 * account cache, which the save writes optimistically and rolls back on failure;
 * it is never disabled while saving (a disabled select drops keyboard focus).
 */
function StatementDayRow({ account, pickerRef }: { account: Account; pickerRef: RefObject<HTMLSelectElement | null> }) {
  const { setDay } = useSetStatementDay()
  const toast = useToast()
  return (
    <>
      <div className="account-detail__row account-detail__row--inline">
        <span className="account-detail__row-label">Statement day</span>
        <Select
          ref={pickerRef}
          label="Statement day"
          size="panel"
          value={account.statementDay ?? ''}
          aria-describedby={NOTE_ID}
          onChange={(e) => {
            const v = e.target.value
            setDay(account.id, v === '' ? null : Number(v)).then(
              () => emit('statement_day_changed', { statement_day_present: v !== '' }),
              (err: unknown) =>
                toast.show({
                  kind: 'error',
                  message: err instanceof AccountsError && err.status === 422 && statementErrorKind(err.body) === 'not_credit'
                    ? 'A statement day can only be set on a credit card.'
                    : 'Could not save the statement day.',
                }),
            )
          }}
        >
          <option value="">Not set</option>
          {STATEMENT_DAYS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </Select>
      </div>
      <p className="note" id={NOTE_ID}>Days 29–31 fall on the last day of shorter months.</p>
    </>
  )
}

/**
 * Owner via `PATCH /accounts/{id}/beneficial-owner`, members from M12's
 * `useMembers`. A hand-rolled `role="menu"`, not `SegmentedControl` — the
 * user's own words, round 6: "it is not in a segment component." This is
 * the SECOND `role="menu"` in `src/`, after `household/RoleMenu.tsx`, whose
 * shape this one borrows rather than inventing a second pattern: trigger +
 * anchored panel, `menuitemradio`/`aria-checked` for the single-select set,
 * `aria-disabled` never native `disabled` on an item (a disabled item
 * cannot receive focus — `RoleMenu`'s own blocking finding), outside-click
 * and Escape both close without acting, first item takes focus on open,
 * the trigger regains it on close.
 *
 * **Narrower than `RoleMenu` in two ways, both deliberate:**
 * - **No phone bottom sheet.** `RoleMenu`'s `Overlay` variant exists for
 *   M12-R20, a card this screen has no equivalent of — `SettingsSection`
 *   already puts the whole panel behind its OWN phone disclosure, and a
 *   second, nested overlay inside that was not asked for. One anchored
 *   panel at every breakpoint.
 * - **No "last owner" guard.** `RoleMenu`'s `blocked`/`aria-disabled` exist
 *   because demoting a household's sole owner is refused server-side; every
 *   option here — including `Unclaimed` — is always a legal choice, so
 *   nothing here plays that role.
 *
 * **`OwnerPicker`/`SegmentedControl` is retired, not kept behind a further
 * reveal.** Checked for other callers before removing it (`grep -rn
 * OwnerPicker src/`) — this was the only one. Ticket 5's "keep unchanged"
 * list named that control to stop it being DROPPED; the capability
 * (`PATCH .../beneficial-owner`, M5-R17) survives here, just through a
 * different control, which is what the user asked for directly.
 *
 * **The trigger's own text is the value**, exactly as `RoleMenu`'s trigger
 * shows the current role rather than a generic "Change" label — reading
 * the row does not require opening the menu first. `.owner-menu__trigger`
 * (`accounts.css`) allows it to wrap/truncate (#422: an email fallback is
 * an unbreakable token that widened this panel once already).
 */
function OwnerMenu({ account }: { account: Account }) {
  const [open, setOpen] = useState(false)
  const householdId = useHouseholdId()
  const membersState = useMembers(householdId.id)
  const setOwner = useSetBeneficialOwner()
  const toast = useToast()
  const rootRef = useRef<HTMLSpanElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const current = account.ownerId ?? UNCLAIMED

  // Outside click and Escape both close the menu without acting — the same
  // pair `RoleMenu`'s own docblock explains (CLAUDE.md's a11y bar asks for
  // a real keyboard path, not just a way in).
  useEffect(() => {
    if (!open) return
    function onDocumentPointerDown(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false)
    }
    function onDocumentKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', onDocumentPointerDown)
    document.addEventListener('keydown', onDocumentKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocumentPointerDown)
      document.removeEventListener('keydown', onDocumentKeyDown)
    }
  }, [open])

  // Re-runs when `membersState.status` changes while open, so a menu opened
  // mid-loading still moves focus in once the real items replace the
  // skeleton — the ARIA menu-button pattern opens onto its first item, not
  // onto nothing (`RoleMenu`'s own reasoning; jsdom does not enforce a
  // disabled item refusing focus the way Chromium does, which is why no
  // item here is ever native `disabled`).
  useEffect(() => {
    if (open) itemRefs.current[0]?.focus()
  }, [open, membersState.status])

  function moveFocus(from: number, delta: number) {
    const count = itemRefs.current.length
    if (count === 0) return
    const next = (from + delta + count) % count
    itemRefs.current[next]?.focus()
  }

  function onItemKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      moveFocus(index, 1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      moveFocus(index, -1)
    }
  }

  function choose(value: string) {
    setOpen(false)
    triggerRef.current?.focus()
    if (value === current) return
    void setOwner
      .setOwner(account.id, value === UNCLAIMED ? null : value)
      .catch(() => toast.show({ kind: 'error', message: 'Could not change the owner.' }))
  }

  return (
    <span className="owner-menu" ref={rootRef}>
      <Button
        ref={triggerRef}
        variant="chip"
        size="panel"
        className="owner-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {account.ownerName ?? 'Unclaimed'}
        <Icon glyph={ChevronDown} />
      </Button>
      {open && (
        // #425 T4 (gate finding): `role="menu"` was on THIS wrapper,
        // unconditionally -- but a menu may only own `menuitem`/
        // `menuitemradio`/`menuitemcheckbox` children, and `<Async>` renders
        // its OWN markup in every state regardless (`Async.tsx`'s
        // `role="status"` live region and `aria-busy` div exist on every
        // branch; loading owns only `Skeleton`s, error only an
        // `AlertPanel`). A `role="presentation"` wrapper does not fix this
        // -- ARIA ownership follows descent, not directness, the same
        // finding `InstitutionsPanel.tsx`'s own `role="list"` docblock
        // records for the identical shape -- so this wrapper carries no
        // role at all now. `role="menu"`/`aria-label="Owner"` move onto a
        // second, inner element that exists ONLY inside the render prop,
        // where the real `menuitemradio` options are the only children.
        // Loading/empty/error still render here, inside
        // `.owner-menu__panel`, just without a `menu` role wrapping them --
        // rule 10 stays intact, `household-members` stays `live`.
        <div className="owner-menu__panel">
          {/* B2 (architect review, #396): `membersState` used to be
              discarded for every status but `success` -- the states
              already exist (`useMembers` returns a full
              AsyncState<Member[]>) and `household-members` is already
              registered `live` (`surfaces.ts`). Nested inside the screen's
              own `surface="accounts"` Async is fine -- different surfaces,
              and `live` vs `synced` is exactly what the registry exists to
              keep straight. Same call `Household.tsx` already makes for the
              identical hook. */}
          <Async
            surface="household-members"
            state={membersState}
            skeleton={
              <>
                <Skeleton height={30} />
                <Skeleton height={30} />
              </>
            }
            // Unreachable -- `useMembers` passes no `isEmpty` to `fromQuery`;
            // the signed-in user reading this screen is necessarily a
            // household member. Required anyway, matching `Household.tsx`'s
            // own reasoning: `Async`'s `empty` prop is mandatory so the
            // state cannot be forgotten by a future caller that does filter.
            empty={{ title: 'No members yet.' }}
          >
            {(members) => {
              const options = [
                { value: UNCLAIMED, label: 'Unclaimed' },
                ...members.map((m) => ({ value: m.userId, label: m.displayName ?? m.email ?? 'Member' })),
              ]

              return (
                <div role="menu" aria-label="Owner">
                  {options.map((option, index) => (
                    <Button
                      key={option.value}
                      ref={(el) => {
                        itemRefs.current[index] = el
                      }}
                      role="menuitemradio"
                      aria-checked={option.value === current}
                      variant="chip"
                      size="panel"
                      className="owner-menu__item"
                      onKeyDown={(event) => onItemKeyDown(event, index)}
                      onClick={() => choose(option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              )
            }}
          </Async>
        </div>
      )}
    </span>
  )
}

/**
 * M5-R28: "a full screen on phone, with settings in a collapsible section
 * below the balance." Collapsed by default below 640px — `useBreakpoint()`,
 * never `window.innerWidth` or a stylesheet media query (rule 5). Tablet and
 * up render the plain heading with the content always visible, unchanged
 * from before this section existed.
 *
 * **A real `<button aria-expanded>`, not a styled `div`** — team-lead's own
 * condition on #396: a disclosure a keyboard or AT user cannot operate is
 * the same defect wearing a different hat as `AccountRow`'s own onActivate
 * fix a few commits back. The content stays MOUNTED and gets `hidden`
 * rather than being conditionally rendered, so `aria-controls` always
 * resolves to a real element regardless of expanded state.
 */
function SettingsSection({
  account,
  manual,
  syncable,
  syncPending,
  nextSyncAt,
  onSync,
  onHide,
  expanded,
  onExpandedChange,
  statementDayRef,
}: {
  /** The phone disclosure's state lives in the page so "Set statement day" can open it. */
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
  statementDayRef: RefObject<HTMLSelectElement | null>
  account: Account
  manual: boolean
  /** False for a disconnected account: its item is gone or provider-removed
   *  (a sync answers 410), so no Sync now is offered (#534). */
  syncable: boolean
  syncPending: boolean
  nextSyncAt: Date | null
  onSync: () => void
  onHide: () => void
}) {
  const phone = useBreakpoint() === 'phone'
  // 'page': a full-width page-level control, like AccountCard's name link.
  // The floor comes from useHitTarget, the one breakpoint-aware source; the
  // disclosure rule's `display: flex` is what lets the min-height apply.
  const minHeight = useHitTarget('page')
  const contentId = useId()
  const syncReasonId = useId()

  return (
    <Panel>
      {phone ? (
        // B4 (architect review, #396): the heading wraps the button, never
        // the reverse -- `button` is a role whose descendants are
        // presentational, so a heading INSIDE one stops being a heading, and
        // that is exactly backwards on the width where the page is longest.
        <h3 className="account-form__title">
          <button
            type="button"
            className="account-detail__disclosure hoverable"
            style={{ minHeight }}
            aria-expanded={expanded}
            aria-controls={contentId}
            onClick={() => onExpandedChange(!expanded)}
          >
            Account settings
            <span
              className={
                expanded
                  ? 'account-detail__chevron account-detail__chevron--open chevron-rotate'
                  : 'account-detail__chevron chevron-rotate'
              }
            >
              <Icon glyph={ChevronDown} />
            </span>
          </button>
        </h3>
      ) : (
        <h3 className="account-form__title">Account settings</h3>
      )}
      <div id={contentId} hidden={phone && !expanded}>
        <OwnerRow account={account} />
        {/* Four identity/metadata rows — user request, design gap-fill round
            4, 2026-09-17, all from fields `AccountResponse` already carries
            (verified against `openapi.json`; none invented). Unconditional
            on `manual` — these describe the account itself, not its sync
            state, so both provider kinds show them; only the sync/action
            block below is gated on `!manual`. Ordered so the identity facts
            group together above the sync/action block, per the brief. */}
        <div className="account-detail__row account-detail__row--inline">
          <span className="account-detail__row-label">Account type</span>
          {/* `subtype` deliberately NOT shown. It has no label map anywhere
              in this app — `accountGroups.ts`'s own `MANUAL_ACCOUNT_TYPES`
              covers `type` only — and its real values are raw Plaid
              strings (`checking`, `hsa`, `401k`…) that title-case into
              either fine ("Checking") or wrong ("Hsa", not "HSA") results
              with no way to tell which from the string alone. A guessed
              capitalisation table is the "invent a subtype label table"
              this was told not to do; the mapped `type` alone is the
              honest amount of detail until a real design exists for it. */}
          <span className="account-detail__row-value">{accountTypeLabel(account.type)}</span>
        </div>
        {account.institutionName && (
          // Duplicates the hero's own `Institution · ••mask` line
          // (`AccountHero.tsx`) — a deliberate, visible choice, not an
          // oversight: the user asked for the row, and both read
          // `account.institutionName` directly, so the two cannot disagree.
          <div className="account-detail__row account-detail__row--inline">
            <span className="account-detail__row-label">Institution</span>
            <span className="account-detail__row-value">{account.institutionName}</span>
          </div>
        )}
        {providerLabel(account.provider) && (
          <div className="account-detail__row account-detail__row--inline">
            <span className="account-detail__row-label">Data source</span>
            <span className="account-detail__row-value">{providerLabel(account.provider)}</span>
          </div>
        )}
        <div className="account-detail__row account-detail__row--inline">
          <span className="account-detail__row-label">Currency</span>
          {/* The CODE (`CAD`), plain text — metadata, not an amount, so
              rule 4 ("only Money renders currency") does not apply; there
              is no cents value here for `Money` to format. */}
          <span className="account-detail__row-value">{account.currency}</span>
        </div>
        {isCreditCard(account.type) && <StatementDayRow account={account} pickerRef={statementDayRef} />}
        {/* M5-R7 again, here: no sync affordance for either provider on a
            manual account, and no Hide account either — a manual account was
            never connected to a bank in the first place, so there is no
            connection to sync or hide. */}
        {!manual && (
          <>
            {/* Replaces the artboard's "Sync frequency · Every 6 hours"
                (design gap-fill, 2026-09-17) — see `lastSyncedValue`'s own
                docblock for why that row describes nothing real.
                `next sync available …` only appears once this SESSION'S own
                Sync-now click has returned a `next_sync_at` — nothing on the
                account's own GET response carries it, so there is nothing to
                show before that happens. */}
            <div className="account-detail__row account-detail__row--inline">
              <span className="account-detail__row-label">Last synced</span>
              <span className="account-detail__row-value">{lastSyncedValue(account, nextSyncAt)}</span>
            </div>
            <div className="account-detail__actions">
              {/* B3 (architect review, #396): two conditions were folded into
                  one `disabled`. `syncPending` is temporary and self-explains
                  via the "Syncing…" label -- native `disabled` stays for that
                  alone. `itemId === null` is PERSISTENT and explains nothing on
                  its own, so it moves to `aria-disabled` plus `DisabledReason`
                  (#373's doctrine) rather than either hiding the button --
                  which would collide with M5-R7's "no Sync now" meaning
                  MANUAL account, the property `AccountDetail.test.tsx` already
                  asserts -- or leaving a permanently dimmed control unexplained. */}
              {!syncable ? null : account.itemId === null ? (
                // N1 (architect round 2, #396, non-blocking): adjacent is not
                // BOUND. `aria-describedby` pairs with `aria-disabled` so the
                // reason arrives WITH the control at focus time, matching
                // `Household.tsx`'s leave button -- the in-repo shape that
                // already does both.
                <DisabledReason
                  reason="This account isn't linked to a bank connection."
                  reasonId={syncReasonId}
                >
                  <Button
                    variant="chip"
                    aria-disabled
                    aria-describedby={syncReasonId}
                    onClick={onSync}
                  >
                    Sync now
                  </Button>
                </DisabledReason>
              ) : (
                <Button variant="chip" disabled={syncPending} onClick={onSync}>
                  {syncPending ? 'Syncing…' : 'Sync now'}
                </Button>
              )}
              {/* `variant="destructive"` (design gap-fill, 2026-09-17,
                  pulled forward from Ticket 5) — `1px --neg` border, `--neg`
                  text on a `--neg-soft` fill, matching the artboard's
                  intent that the irreversible action reads differently
                  from the routine one. The variant already ships in
                  `buttonSkin.ts`/`primitives.css` (`Merchants.tsx`'s own
                  Delete button is the live precedent) — no new skin, no new
                  variant. Contrast against THIS backdrop (an opaque `Panel`,
                  never the lime hero rule 9 would forbid it on) is pinned in
                  `compositeContrast.test.tsx`: 6.14:1 light / 7.18:1 dark
                  text, 7.00:1 light / 9.43:1 dark border — comfortably past
                  both floors in both themes. */}
              <Button variant="destructive" onClick={onHide}>
                {accountRemovalCopy(account).action}
              </Button>
            </div>
          </>
        )}
      </div>
    </Panel>
  )
}

/** `en-US`, month-then-day ("Jul 28") — the artboard's own date shape for this
 *  panel specifically, distinct from `MerchantTransactions.tsx`'s "28 Jul
 *  2026" (`en-GB`, day-first, year included): that screen's rows have their
 *  own room and no sibling date already visible; this row sits beside a
 *  balance-history chart whose own axis already reads "jul", so a shorter,
 *  yearless form matches the density the design actually draws rather than
 *  reusing a different screen's convention wholesale. */
const TX_DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })

/** A non-empty string, trimmed — `MerchantTransactions.tsx`'s own `hasText`,
 *  repeated rather than imported for the identical reason its docblock
 *  gives: no shared home either file would agree belongs to it more than
 *  its own. */
function hasText(s: string | null | undefined): s is string {
  return s != null && s.trim() !== ''
}

/** `personal_finance_category_primary` arrives upstream-shaped
 *  (`FOOD_AND_DRINK`) — `MerchantTransactions.tsx`'s own humaniser, repeated
 *  for the same "no shared home" reason `hasText` is. */
function humanizeProviderCategory(value: string): string {
  const lower = value.replace(/_/g, ' ').toLowerCase()
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

/**
 * "Transactions on this account" — pulled forward from M6 by explicit user
 * instruction (design gap-fill, 2026-09-17), following `MerchantTransactions
 * .tsx`'s own shape for the identical concern: `DataGrid`, `Money
 * kind="flow"` for a signed amount, the same "N of Total" honesty when the
 * preview is a subset (`useAccountTransactions.ts`'s own `PREVIEW_PAGE_SIZE`).
 *
 * **No "See all →" link.** #425 §B5 draws one, to `/transactions?account=`
 * — a route `App.tsx` does not declare; M6 has not shipped. A link to a
 * route the router does not have is the exact trap CLAUDE.md names
 * ("a criterion naming a route does not make it exist"), so it is omitted
 * here rather than built, matching this module's own precedent for
 * "Import statement" (`Accounts.tsx`).
 *
 * **`kind="flow"`, not `stock`.** The artboard signs every row
 * (`−$118.44`/`+$4,280.00`), and `Money`'s flow branch's known #436 (a
 * hardcoded `--ink` for a non-positive figure, unsafe only on a `HeroTile`)
 * does not apply here — this panel sits on a plain opaque `Panel`, never the
 * lime hero, so the inline `--ink` `Money.tsx` sets for that branch is
 * exactly the surface it was always correct on.
 *
 * **`account-transactions`, a `live` surface** (`surfaces.ts`) —
 * `TransactionResponse` carries no `last_synced_at`, same reasoning as
 * `merchant-transactions`.
 */
function AccountTransactionsPanel({ account }: { account: Account }) {
  const txState = useAccountTransactions(account.id)
  const categoriesState = useCategories()

  // A second, independent observer of the shared categories cache — never a
  // second fetch (`useCategories`'s own docblock: one `queryKey` for every
  // caller). This panel's own loading/error/empty states are never gated on
  // it: a category that has not resolved yet falls through to the row's own
  // "Uncategorized" branch below rather than blocking a surface that is
  // otherwise ready to show.
  const categoryById = useMemo(() => {
    const map = new Map<string, Category>()
    if (categoriesState.status === 'success') {
      for (const c of categoriesState.data) map.set(c.id, c)
    }
    return map
  }, [categoriesState])

  /** Resolved once per row, read by both the Transaction column's icon and
   *  the Category column's own text — a single source so the glyph and the
   *  word next to it can never name two different categories. */
  function categoryOf(t: AccountTransaction): { category: Category | undefined; name: string } {
    const category = t.categoryId ? categoryById.get(t.categoryId) : undefined
    const name = category
      ? category.name
      : hasText(t.personalFinanceCategoryPrimary)
        ? humanizeProviderCategory(t.personalFinanceCategoryPrimary)
        : 'Uncategorized'
    return { category, name }
  }

  const columns: Column<AccountTransaction>[] = [
    {
      // Four real columns, not one crammed meta line (user finding, design
      // gap-fill round 5, 2026-09-17): category/owner/date used to be joined
      // into one small line under the name while the grid's own middle sat
      // empty (two columns, `minmax(0,1fr)` + Amount, at any width wider
      // than the two together). Category and Date now have their own
      // columns; Owner stays on the name's own meta line — it names a
      // PERSON, which reads as part of the transaction's identity the way
      // `MerchantTransactions.tsx`'s raw-descriptor line sits under ITS own
      // name, and a fifth column for a single short word left the four data
      // columns cramped at tablet widths when tried.
      key: 'transaction',
      header: 'Transaction',
      // `minmax(0, …)`, not a pixel floor — every other `DataGrid` in this
      // repo (`MerchantTransactions.tsx`, `CategoryTable.tsx`) uses a zero
      // floor for exactly this reason: a non-zero floor on EVERY column
      // sums to a track total `minmax()` can never shrink below, and at
      // 320px that total clipped both edges of the panel instead of
      // scrolling inside `.account-transactions__scroll` the way a
      // zero-floor grid does (team-lead finding, screenshotted). `84px`
      // fixed widths stay legitimate for a genuinely fixed column
      // (`CategoryTable.tsx:203`'s own precedent) — none of these four are
      // that; all carry text that must be free to shrink.
      //
      // Amended 2026-09-17: `amount` now carries a `max-content` floor, for
      // the reason its own comment gives. That does not weaken the rule
      // above — the rule is about a floor on EVERY column, and three of the
      // four still shrink to zero, so the total remains shrinkable.
      width: 'minmax(0, 1.8fr)',
      render: (t) => {
        const { category } = categoryOf(t)
        return (
          <span className="account-transactions__tx">
            {/* `aria-hidden` — Category is now its own column, so the glyph
                would otherwise announce a second, redundant name, the same
                call `CategoryIcon.tsx`'s own docblock makes for every row
                that already shows its category as text. `category ??
                { name: 'Uncategorized' }` matches `CategoryIcon`'s own
                name-keyed fallback branch rather than inventing a second
                "no category" rendering. */}
            <span className="account-transactions__tx-icon" aria-hidden="true">
              <CategoryIcon category={category ?? { name: 'Uncategorized' }} />
            </span>
            <span className="account-transactions__tx-text">
              <span className="account-transactions__tx-name">{t.name}</span>
              {hasText(t.ownerName) && (
                <span className="account-transactions__tx-meta">{t.ownerName}</span>
              )}
            </span>
          </span>
        )
      },
    },
    {
      key: 'category',
      header: 'Category',
      width: 'minmax(0, 0.9fr)',
      render: (t) => <span className="account-transactions__cell">{categoryOf(t).name}</span>,
    },
    {
      key: 'date',
      header: 'Date',
      width: 'minmax(0, 0.6fr)',
      render: (t) => (
        <span className="account-transactions__cell">
          {TX_DATE.format(new Date(`${t.date}T00:00:00`))}
        </span>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      // The ONE column with a floor, and the comment on `transaction` above
      // explains why that is not the failure it warns about. Its argument
      // holds for a floor on EVERY column — four floors sum to a total
      // `minmax()` can never shrink below, which is what clipped both panel
      // edges at 320px. One floor among three zero-floored siblings keeps the
      // total shrinkable: the description, category and date tracks give up
      // width instead, and measured at 320px the grid still fits its scroll
      // box with 0px of overflow.
      //
      // It earns the floor because its content is the only cell here that
      // MUST NOT be cut: `+$6,500.00` in tabular numerals does not wrap and
      // does not ellipsise, so a zero floor let it spill and be clipped — 16px
      // lost at 390px, 26px at 320px, and the scroller could not even scroll
      // to it because the track total still equalled the container. A cut
      // amount is a wrong number on screen, not a cosmetic overflow. Found in
      // Sushil's screenshot, 2026-09-17; measured before and after with a
      // `max-content` floor injected, then landed.
      width: 'minmax(max-content, 0.6fr)',
      align: 'end',
      // `kind="flow"` — see this component's own docblock for why #436 does
      // not apply on this (non-hero) surface.
      render: (t) => <Money cents={t.amountCents} currency={t.currency} kind="flow" />,
    },
  ]

  return (
    <Panel>
      {/* `account-transactions__title` alongside the shared class — see its
          own CSS docblock for why this one heading needs a bottom margin
          the other two `.account-form__title`s on this page do not
          (team-lead finding, screenshot-verified, design gap-fill pass). */}
      <h3 className="account-form__title account-transactions__title">
        Transactions on this account
        {/* Architect I8's own condition, reused here: the fetch asks for a
            fixed preview and the count comes from `total`, which the
            response already carries — no second request, no new control.
            Named only when it actually bites. */}
        {txState.status === 'success' && txState.data.total > txState.data.transactions.length && (
          <span className="account-form__title-count">
            {' · '}
            {txState.data.transactions.length} of {txState.data.total}
          </span>
        )}
      </h3>
      <Async
        surface="account-transactions"
        state={txState}
        skeleton={
          <>
            <Skeleton height={44} />
            <Skeleton height={44} />
            <Skeleton height={44} />
          </>
        }
        empty={{ title: 'No transactions on this account yet.' }}
      >
        {(page) => (
          // `overflow-x: auto` — a fourth column is exactly the #130/#423
          // narrow-width shape (user finding, design gap-fill round 5):
          // `DataGrid` itself has no horizontal-scroll container of its own
          // (`dataGrid.css` only ever scrolls vertically), and four columns'
          // own `minmax()` floors cannot all shrink to fit a 320px panel.
          // Scrolling THIS box keeps the overflow inside the panel rather
          // than widening the page — checked directly at 320px (this
          // report), not left for a sweep to find.
          <div className="account-transactions__scroll">
            <DataGrid
              columns={columns}
              groups={[{ label: '', rows: page.transactions }]}
              rowKey={(t) => t.id}
              label="Transactions on this account"
              empty="No transactions on this page."
            />
          </div>
        )}
      </Async>
    </Panel>
  )
}

const SKELETON = (
  <>
    <Skeleton height={80} />
    <Skeleton height={44} />
    <Skeleton height={44} />
  </>
)

/**
 * `/accounts/:id` — Ticket 2. Hero balance, health, settings
 * (Owner/Sync now/Hide account), the health note above.
 *
 * **`useParams()`, not `useParams<{ id: string }>()`** — `CategoryDetail.tsx`'s
 * own reasoning: the router types a param as possibly absent, and a generic
 * asserting it away moves the lie from the router's types to this file's.
 * `useAccount(undefined)` stays parked (`enabled: id !== undefined`) and the
 * surface renders its own loading skeleton rather than a second "not found".
 */
export function AccountDetail() {
  const { id } = useParams()
  const state = useAccount(id)
  const navigate = useNavigate()
  const toast = useToast()
  const sync = useSyncItem()
  // Hero/Settings are unequal columns now (design gap-fill, 2026-09-17 —
  // Settings is noticeably wider than the hero in the artboard), and an
  // asymmetric `fr` pair does not collapse to one column on its own the way
  // `repeat(auto-fit, minmax(…))`'s equal, repeated track does — two
  // differently-sized explicit tracks just compress side by side past their
  // combined floor rather than reflowing. `useBreakpoint()`, the one legal
  // source (rule 5), stacks it explicitly instead: `tablet` and `phone` are
  // both narrower, after `ChromeLayout`'s own sidebar and padding, than the
  // ~660-700px two 330px-floored columns need (#425 T4 gate N-4's own
  // finding, on the equal-column version of this grid).
  const breakpoint = useBreakpoint()
  const stackGrid = breakpoint !== 'desktop' && breakpoint !== 'wide'

  // The last sync outcome THIS SESSION saw — `next_sync_at` is not on
  // `AccountResponse` at all (only `ItemSyncResponse`, the sync mutation's
  // own response), so there is nothing to read it from before a Sync-now
  // click has actually happened once. `useSyncItem`'s own cache invalidation
  // (`keys.accounts.all()`) refetches the account afterwards for
  // `lastSyncedAt`; this is the one field that invalidation cannot recover,
  // since nothing GETs it.
  const [nextSyncAt, setNextSyncAt] = useState<Date | null>(null)
  const [settingsExpanded, setSettingsExpanded] = useState(false)
  const statementDayRef = useRef<HTMLSelectElement>(null)

  // Hide account (not "Disconnect": `DELETE /accounts/{id}` only sets
  // `is_hidden`, the bank link is untouched — the real Disconnect is
  // `/connections`', #499) reuses the SAME two-phase delete flow `Accounts.tsx` uses
  // (issue #396, carried over from #395) — extracted before this screen
  // existed so there is never a second, drifting copy of the 409 retry.
  // Unlike the list, this screen is ABOUT one account: a successful delete
  // leaves nothing here to show, so it navigates back rather than closing a
  // dialog over a page that would otherwise render stale.
  const deleteFlow = useAccountDeleteFlow((account) => {
    toast.show({ kind: 'success', message: accountRemovalCopy(account).toast })
    navigate('/accounts')
  })
  const copy = deleteFlow.target ? accountRemovalCopy(deleteFlow.target) : null

  return (
    // N2/N3 (architect review, #396, non-blocking): the back link and the
    // Async body sit PAGE-LEVEL, outside any `<Panel>` -- the same split
    // `MerchantTransactions.tsx` already draws under #302. A single outer
    // `<Panel>` here used to wrap Settings' and Balance history's own
    // content Panels together, and `.surface-box`'s `align-items: stretch`
    // would have stretched a standalone `InlineLink` to the panel's full
    // width the same way it did there.
    <>
      {/* Rendered unconditionally, above `<Async>`, so it stays reachable
          while the account itself is still loading or has failed to --
          `MerchantTransactions.tsx`'s own reasoning for its identical link.
          This screen has no nav entry of its own; a row click is the only
          way in today, so without this there was no way back out.

          #425 B1: a 30px `--chip` circle plus the text, both activating
          `/accounts` — as ONE link, not two. B1's own artboard shows two
          separate hit areas ("[←] ... + the text ... both activating"),
          folded here into a single `InlineLink` rather than two adjacent
          controls to the same destination, which would be a keyboard-
          tabbing regression the design does not intend.

          **Wording: "All accounts", matching B1 — settled, not left open.**
          An earlier version of this comment kept the old "Back to accounts"
          text on the grounds that a locked e2e spec pinned it. Team-lead's
          ruling (#425 T4 re-review): `docs/rules.md`'s "sources, not the
          system" governs VALUES — the rule that stops a canvas hex being
          inlined — not COPY, which is exactly what an artboard has
          authority over; the tester has re-pointed
          `account-detail.spec.ts`'s own assertion to `/all accounts/i` to
          match. */}
      <div className="account-detail__back">
        <BackLink to="/accounts">All accounts</BackLink>
      </div>
      <Async
        surface="accounts"
        state={state}
        skeleton={SKELETON}
        // Required anyway, `Async`'s own docblock: `useAccount` passes no
        // `isEmpty`, so this branch cannot be reached today, but the prop
        // stays so a future caller that DOES filter cannot forget it.
        empty={{ title: 'Account not found.' }}
      >
        {(account) => {
          const manual = account.provider === 'manual'

          return (
            <>
              {/* M5-R13's reauth caution sits ABOVE the two-column grid below
                  — a caution about the connection as a whole reads oddly
                  wedged between the hero and Settings once they sit side by
                  side, and it applies to neither column specifically. */}
              <HealthNote account={account} />

              {/* #425 B2: Hero and Settings side by side above `desktop`,
                  stacked below — unequal columns now (design gap-fill,
                  2026-09-17), Settings 1.4fr against the hero's 1fr, each
                  still floored at `min(330px, 100%)` (Ticket 3's card-grid
                  fix, so the 320px sweep cannot overflow a fixed minmax
                  floor). An asymmetric `fr` pair does not collapse to one
                  column via `auto-fit`'s own repeated-track mechanism the
                  way the old EQUAL-column version did, so `stackGrid`
                  (`useBreakpoint()`, the one legal source) applies the
                  explicit stack instead — see this component's own comment
                  on that variable. */}
              <div className={`account-detail__grid${stackGrid ? ' account-detail__grid--stacked' : ''}`}>
                <AccountHero account={account} />

                <SettingsSection
                  expanded={settingsExpanded}
                  onExpandedChange={setSettingsExpanded}
                  statementDayRef={statementDayRef}
                  account={account}
                  manual={manual}
                  syncable={accountHealth(account).kind !== 'disconnected'}
                  syncPending={sync.isPending}
                  nextSyncAt={nextSyncAt}
                  onSync={() => {
                    if (account.itemId === null) return
                    // Once per press, matching `Accounts.tsx`'s `useSyncAll`
                    // (B1, architect review, #399) — this is already the
                    // single-item case, so there is no fan-out to record.
                    emit('sync_triggered', { scope: 'item' })
                    sync
                      .sync(account.itemId)
                      .then((result) => {
                        setNextSyncAt(result.nextSyncAt)
                        toast.show({ kind: 'success', message: summarizeSync(result) })
                      })
                      .catch(() => toast.show({ kind: 'error', message: 'Sync could not be started.' }))
                  }}
                  onHide={() => deleteFlow.start(account)}
                />
              </div>

              {/* "This cycle": a credit card's open statement cycle. Renders
                  nothing for any other account, or when the backend says
                  hidden. */}
              <StatementCycleCard
                account={account}
                onSetStatementDay={() => {
                  // Commit the expansion first: a `hidden` select cannot take focus.
                  flushSync(() => setSettingsExpanded(true))
                  const picker = statementDayRef.current
                  if (picker === null) return
                  picker.focus()
                  picker.scrollIntoView({ block: 'center' })
                }}
              />

              {/* Balance history — Step 3. Blocked in the spec (no endpoint),
                  fixture-backed; see BalanceHistory.tsx's own docblock.
                  Title lives INSIDE BalanceHistory now (design gap-fill
                  round 6) — it shares a row with the range control, which
                  only that component has the state to render. */}
              <Panel>
                <BalanceHistory account={account} />
              </Panel>

              {/* Transactions on this account — pulled forward from M6 by
                  explicit user instruction; see AccountTransactionsPanel's
                  own docblock. */}
              <AccountTransactionsPanel account={account} />
            </>
          )
        }}
      </Async>

      <ConfirmDialog
        open={deleteFlow.target !== null}
        onCancel={deleteFlow.cancel}
        onConfirm={deleteFlow.confirm}
        errorFor={deleteFlow.errorFor}
        mode={copy?.mode ?? 'hide_account'}
        title={copy?.title ?? ''}
        body={deleteFlow.target ? deleteFlow.bodyFor(deleteFlow.target) : ''}
        confirmLabel={copy?.confirmLabel ?? 'Hide account'}
        destructive
      />
    </>
  )
}
